import { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
  Backdrop
} from '@mui/material';
import { useGetIdentity, useLogout, useRefresh } from 'react-admin';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CasinoIcon from '@mui/icons-material/Casino';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TuneIcon from '@mui/icons-material/Tune';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import RefreshIcon from '@mui/icons-material/Refresh';
import Parse from 'parse';
import { Loader } from '../components/Loader';

const SIDEBAR_WIDTH_OPEN = 260;
const SIDEBAR_WIDTH_CLOSED = 60;

// Sidebar collapsed hover label style
const hoverLabelSx = {
  position: 'absolute',
  left: '60px',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: '#242424',
  color: '#fff',
  padding: '6px 12px',
  borderRadius: '6px',
  fontSize: '13px',
  whiteSpace: 'nowrap',
  opacity: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease',
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
  zIndex: 9999,
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-4px',
    top: '50%',
    transform: 'translateY(-50%) rotate(45deg)',
    width: '8px',
    height: '8px',
    backgroundColor: '#242424'
  }
};

export const CustomLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const logout = useLogout();
  const refresh = useRefresh();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [contentKey, setContentKey] = useState(0);
  const [loggingOut, setLoggingOut] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: identity } = useGetIdentity();
  const [userBalance, setUserBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Roles that should see balance (Game-Owner to Agent)
  const balanceVisibleRoles = [
    'Game-Owner',
    'Master-Distributor',
    'Distributor',
    'Sub-Distributor',
    'Agent'
  ];
  const shouldShowBalance = identity?.role && balanceVisibleRoles.includes(identity.role);
  const isAdmin = identity?.role === 'Admin';
  const isSuperUser = identity?.role === 'Super-User';
  const needsAdminOtp = isAdmin && identity?.adminFirstLoginCompleted === false;
  const isAdminOtpRoute = location.pathname === '/admin-otp-verification';

  const fetchUserBalance = async () => {
    if (!shouldShowBalance) return;
    setBalanceLoading(true);
    try {
      const currentUser = Parse.User.current();
      if (currentUser) {
        await currentUser.fetch();
        setUserBalance(currentUser.get('balance') || 0);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (shouldShowBalance) {
      fetchUserBalance();
    }
  }, [identity?.role]);

  useEffect(() => {
    if (needsAdminOtp && !isAdminOtpRoute) {
      navigate('/admin-otp-verification', { replace: true });
    }
  }, [needsAdminOtp, isAdminOtpRoute, navigate]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      setLoggingOut(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    refresh();
    setContentKey((prev) => prev + 1);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const menuItems = [
    { text: 'Operator Games', icon: <TuneIcon />, path: '/operatorGames' },
    { text: 'Operator Management', icon: <BusinessIcon />, path: '/operators' },
    ...(isSuperUser
      ? [{ text: 'Admin Management', icon: <AdminPanelSettingsIcon />, path: '/admins' }]
      : []),
    { text: 'Game Records', icon: <CasinoIcon />, path: '/GameCatalogue' },
    { text: 'Game Bet Levels', icon: <CurrencyExchangeIcon />, path: '/GameBetConfig' },
    ...(isSuperUser
      ? [{ text: 'Admin Activity Logs', icon: <RefreshIcon />, path: '/admin-activity-logs' }]
      : []),
    { text: 'Profile', icon: <AccountCircleIcon />, path: '/profile' }
  ].filter(
    (item) =>
      !(isAdmin && item.path === '/GameBetConfig') &&
      !(needsAdminOtp && !isAdminOtpRoute && item.path !== '/admin-otp-verification')
  );

  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find((item) => location.pathname === item.path);
    return currentItem ? currentItem.text : 'Vegas Empire';
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (needsAdminOtp && isAdminOtpRoute) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          backgroundColor: '#1A1A1A',
          backgroundImage: 'url(/Assets/Images/login.svg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {children}
      </Box>
    );
  }

  if (needsAdminOtp && !isAdminOtpRoute) {
    return (
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          backgroundColor: 'transparent'
        }}
        open
      >
        <Loader />
      </Backdrop>
    );
  }

  return (
    <>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999,
          backgroundColor: 'transparent'
        }}
        open={isRefreshing}
      >
        <Loader />
      </Backdrop>

      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {!sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: '12px',
              left: '16px',
              zIndex: 1200
            }}
          >
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: '#FFC107',
                backgroundColor: '#242424',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: '#333'
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        )}

        <Box
          sx={{
            width: sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED,
            flexShrink: 0,
            backgroundColor: '#1A1A1A',
            borderRight: sidebarOpen ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
            transition: 'width 0.3s ease',
            overflow: 'visible',
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              width: SIDEBAR_WIDTH_OPEN,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'visible'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                height: '64px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                flexShrink: 0
              }}
            >
              <Box
                component="img"
                src="/Assets/Logos/sidebar-logo.svg"
                alt="Vegas Empire"
                sx={{
                  height: '40px',
                  width: 'auto',
                  opacity: sidebarOpen ? 1 : 0
                }}
              />
              {sidebarOpen && (
                <IconButton onClick={toggleSidebar} size="small" sx={{ color: '#FFC107' }}>
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'visible' }}>
              <List sx={{ overflow: 'visible' }}>
                {menuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5, overflow: 'visible' }}>
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        sx={{
                          px: 2,
                          py: 0.5,
                          position: 'relative',
                          backgroundColor: 'transparent',
                          borderLeft: 'none',

                          ...(sidebarOpen &&
                            isActive && {
                              backgroundColor: 'rgba(255, 193, 7, 0.15)',
                              borderLeft: '4px solid #FFC107'
                            }),

                          '&:hover': {
                            backgroundColor: sidebarOpen ? 'rgba(255, 193, 7, 0.08)' : 'transparent'
                          },

                          '&:hover .hover-label': {
                            opacity: '1 !important',
                            visibility: 'visible !important',
                            transform: 'translateY(-50%) translateX(10px)'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ color: isActive ? '#FFC107' : '#fff', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        {!sidebarOpen && (
                          <Box className="hover-label" sx={hoverLabelSx}>
                            {item.text}
                          </Box>
                        )}
                        {sidebarOpen && (
                          <ListItemText
                            primary={item.text}
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: isActive ? '#FFC107' : '#fff',
                                fontSize: '15px',
                                fontWeight: isActive ? 600 : 500
                              }
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            marginLeft: sidebarOpen ? `${SIDEBAR_WIDTH_OPEN}px` : `${SIDEBAR_WIDTH_CLOSED}px`,
            transition: 'margin-left 0.3s ease'
          }}
        >
          <AppBar
            position="static"
            sx={{
              backgroundColor: '#242424',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Toolbar>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    color: '#FFC107',
                    fontSize: '24px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    marginLeft: sidebarOpen ? '0' : '42px'
                  }}
                >
                  {getCurrentPageTitle()}
                </Box>
              </Box>
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  color: '#fff',
                  mr: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 193, 7, 0.1)',
                    color: '#FFC107'
                  }
                }}
                title="Refresh page"
              >
                {isRefreshing ? (
                  <CircularProgress size={24} sx={{ color: '#FFC107' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
              {identity && (
                <>
                  {shouldShowBalance && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mr: 2
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                      >
                        Balance:
                      </Typography>
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '16px',
                          fontWeight: 600
                        }}
                      >
                        {balanceLoading ? (
                          <CircularProgress size={16} sx={{ color: '#fff' }} />
                        ) : userBalance !== null ? (
                          userBalance.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })
                        ) : (
                          '0.00'
                        )}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    onClick={handleMenuOpen}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 193, 7, 0.08)'
                      }
                    }}
                  >
                    <AccountCircleIcon sx={{ fontSize: '28px', color: '#FFC107' }} />
                    <Box
                      sx={{
                        color: '#fff',
                        fontSize: '15px',
                        fontWeight: 500
                      }}
                    >
                      {identity.fullName || identity.username || 'User'}
                    </Box>
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                      sx: {
                        backgroundColor: '#2A2A2A',
                        border: '1px solid rgba(255, 193, 7, 0.3)',
                        mt: 1,
                        minWidth: 200
                      }
                    }}
                  >
                    <MenuItem
                      onClick={handleLogout}
                      disabled={loggingOut}
                      sx={{
                        color: '#fff',
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 193, 7, 0.1)'
                        },
                        '&:disabled': {
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ color: '#FFC107', minWidth: 36 }}>
                        {loggingOut ? (
                          <CircularProgress size={20} sx={{ color: '#FFC107' }} />
                        ) : (
                          <LogoutIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText primary={loggingOut ? 'Logging out...' : 'Logout'} />
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Toolbar>
          </AppBar>

          <Box
            component="main"
            key={contentKey}
            sx={{
              flexGrow: 1,
              px: 2,
              backgroundColor: '#1A1A1A',
              minHeight: 'calc(100vh - 64px)'
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </>
  );
};
